import {
  BackendPushResponse,
  DatastoreCollectionType,
  DatastoreFetchCollectionType,
  DatastorePushCollectionType,
  DatastoreUpdateCollectionType,
} from '@freelancer/datastore/core';
import { NonObservableQuery } from './non-observable-query';
import {
  MutationPropagator,
  PushTransformer,
  RequestErrorCode,
  SearchTransformer,
  UpdateTransformer,
} from './store.model';

export type IdOrIdsOrQuery<C extends DatastoreCollectionType> =
  | string
  | number
  | readonly string[]
  | readonly number[]
  | ((q: NonObservableQuery<C>) => NonObservableQuery<C>);

/** Methods on DatastoreFake to be used by UI tests. */
export abstract class DatastoreTestingController {
  /**
   * Creates an object directly in the store, without transformations.
   */
  abstract createRawDocument<
    C extends DatastoreCollectionType & DatastorePushCollectionType
  >(
    collectionName: C['Name'],
    document: C['DocumentType'],
  ): Promise<BackendPushResponse<any>>;

  /**
   * Removes data from an entire collection, or an object from a collection.
   * When called with no arguments, resets the whole datastore.
   */
  abstract resetState<C extends DatastoreCollectionType>(
    collectionName?: C['Name'],
  ): Promise<void>;

  /**
   * Output store state for debugging.
   */
  abstract printState(): Promise<void>;

  /**
   * Returns if the datastore has no state for that
   */
  abstract isStateEmpty(): Promise<boolean>;

  /**
   * Add a transformer that will be used when creating new documents of a given
   * collection. Used to specify computed fields that would be returned by a
   * real backend, e.g. object ID.
   */
  abstract addPushTransformer<
    C extends DatastoreCollectionType & DatastorePushCollectionType
  >(collectionName: C['Name'], transformer: PushTransformer<C>): void;

  /**
   * Add a transformer that will be used when updating documents of a given
   * collection. Used to replicate reducers that use the backend response rather
   * than the delta.
   */
  abstract addUpdateTransformer<
    C extends DatastoreCollectionType & DatastoreUpdateCollectionType
  >(collectionName: C['Name'], transformer: UpdateTransformer<C>): void;

  /**
   * Add a propagator that will be used when pushing or updating documents of a
   * given collection. Used to specify that mutations to the original collection
   * also apply to the related collection, e.g. bids and projectViewBids are
   * related, so pushing to the former should also push to the latter.
   */
  abstract addMutationPropagator<
    C1 extends DatastoreCollectionType & DatastorePushCollectionType,
    C2 extends DatastoreCollectionType & DatastorePushCollectionType
  >(propagator: MutationPropagator<C1, C2>): void;

  /**
   * Add a transformer that will be used when search queries are made on a given
   * collection. The transformer allows returning custom search results from
   * existing documents in the store. If one is provided, the order of documents
   * will be preserved.
   */
  abstract addSearchTransformer<C extends DatastoreCollectionType>(
    collectionName: C['Name'],
    transformer: SearchTransformer<C>,
  ): Map<string, SearchTransformer<DatastoreCollectionType>>;

  /**
   * Make all requests to a particular datastore collection fail.
   */
  abstract makeCollectionFail<
    C extends DatastoreCollectionType & DatastoreFetchCollectionType
  >(collectionName: C['Name'], errorCode: RequestErrorCode<C>): void;

  /**
   * Make a specific request to the datastore fail.
   */
  abstract makeRequestFail<
    C extends DatastoreCollectionType & DatastoreFetchCollectionType
  >(
    collectionName: C['Name'],
    idOrIdsOrQuery: IdOrIdsOrQuery<C>,
    errorCode: RequestErrorCode<C>,
  ): void;

  /**
   * Make all requests to a particular datastore collection never return.
   */
  abstract makeCollectionPending<
    C extends DatastoreCollectionType & DatastoreFetchCollectionType
  >(collectionName: C['Name']): void;

  /**
   * Make a specific request to the datastore never return.
   */
  abstract makeRequestPending<
    C extends DatastoreCollectionType & DatastoreFetchCollectionType
  >(collectionName: C['Name'], idOrIdsOrQuery: IdOrIdsOrQuery<C>): void;
}
